import { useCallback } from 'react';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
} from '@atlassian/jira-business-constants';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
	isValidationError,
} from '@atlassian/jira-fetch';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { UPDATE_ISSUE_GROUPED_BY_FIELD_EXPERIENCE, NULL_GROUP_KEY } from '../../common/constants';
import type { BoardColumn } from '../../common/types';
import { useBoardStore, useGetIssueById } from '../../services/board-store';
import type { Issue } from '../../services/board-store/types';
import { useUpdateIssueField } from '../../services/update-issue-field-old';
import { useGroupBy } from '../group-by';

const updateIssueFieldAction = 'jwm.board.issue.field.update';

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardGroupMove = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [getIssueById] = useGetIssueById();
	const [{ issuesWithFields }, { pushToJobQueue }] = useBoardStore();
	const updateIssueField = useUpdateIssueField();
	const [groupBy] = useGroupBy();

	const startExperience = useExperienceStart(UPDATE_ISSUE_GROUPED_BY_FIELD_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(UPDATE_ISSUE_GROUPED_BY_FIELD_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(UPDATE_ISSUE_GROUPED_BY_FIELD_EXPERIENCE);

	const analyticsSuccess = useCallback(() => {
		markExperienceSuccess();
		fireUIAnalytics(createAnalyticsEvent({}), `${updateIssueFieldAction} success`, {
			groupBy,
		});
	}, [createAnalyticsEvent, groupBy, markExperienceSuccess]);

	const analyticsFailure = useCallback(
		(error: Error) => {
			// Exclude client fetch, validation and 4xx errors from experience fail events. Error message for
			// transition and rank is displayed at board level.
			const reportedError =
				error &&
				(isClientFetchError(error) || isValidationError(error) || isHttpClientErrorResponse(error));
			if (!reportedError) {
				markExperienceFailed('Issue transition failed.', error);
				fireUIAnalytics(createAnalyticsEvent({}), `${updateIssueFieldAction} failure`, {
					groupBy,
				});
			} else {
				markExperienceSuccess();
			}
		},
		[createAnalyticsEvent, groupBy, markExperienceFailed, markExperienceSuccess],
	);

	const getUpdateIssueFieldPromise = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(issueId: number, targetColumn: BoardColumn): (() => Promise<any>) | null => {
			const issue = getIssueById(issueId);
			switch (groupBy) {
				case GROUP_BY_PRIORITY:
					return () =>
						updateIssueField({
							issueKey: issue.key,
							fieldKey: 'priority',
							newValue: {
								priorityId: targetColumn.groupKey,
								name: targetColumn.name,
							},
						});
				case GROUP_BY_ASSIGNEE:
					return () =>
						updateIssueField({
							issueKey: issue.key,
							fieldKey: 'assignee',
							newValue: {
								accountId:
									targetColumn.groupKey === NULL_GROUP_KEY ? undefined : targetColumn.groupKey,
								displayName: targetColumn.name,
							},
						});
				case GROUP_BY_CATEGORY:
					return () =>
						updateIssueField({
							issueKey: issue.key,
							fieldKey: targetColumn.groupFieldKey ?? '',
							newValue:
								targetColumn.groupKey === NULL_GROUP_KEY
									? null
									: {
											value: targetColumn.name,
										},
						});
				default:
					return null;
			}
		},
		[getIssueById, groupBy, updateIssueField],
	);

	const isIssueInCategory = useCallback(
		(categoryName: string | undefined, issue: Issue) => {
			const issueDetails = issuesWithFields?.[issue.id];

			const categoryField = issueDetails?.fieldsByAlias.category;
			return (
				categoryField?.value === categoryName ||
				(categoryField?.value == null && categoryName === NULL_GROUP_KEY)
			);
		},
		[issuesWithFields],
	);

	const getMoveBetweenGroupsPromise = useCallback(
		(
			issueId: number,
			targetColumn: BoardColumn,
			getRankPromise: (issueId: number) => () => Promise<void>,
		): Promise<void> => {
			const issue = getIssueById(issueId);
			if (targetColumn == null || issue == null) {
				return Promise.resolve();
			}

			const updateIssueFieldExecutor = getUpdateIssueFieldPromise(issueId, targetColumn);
			// If executor exist is that we can handle this case
			if (updateIssueFieldExecutor) {
				const rankPromiseExecutor = getRankPromise(issueId);
				const isMovingInTheSameColumn =
					(groupBy === GROUP_BY_PRIORITY && targetColumn.groupKey === issue.priorityId) ||
					(groupBy === GROUP_BY_ASSIGNEE && targetColumn.groupKey === issue.assigneeKey) ||
					(groupBy === GROUP_BY_CATEGORY && isIssueInCategory(targetColumn.groupKey, issue));
				if (isMovingInTheSameColumn) {
					return rankPromiseExecutor();
				}

				return new Promise((resolve, reject) => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'issueGroup updated', {
						field: groupBy?.toLowerCase(),
					});
					startExperience();
					updateIssueFieldExecutor()
						.then(rankPromiseExecutor)
						.then((value) => {
							analyticsSuccess();
							resolve(value);
						})
						.catch((error) => {
							analyticsFailure(error);
							reject(error);
						});
				});
			}

			return Promise.resolve();
		},
		[
			getIssueById,
			getUpdateIssueFieldPromise,
			groupBy,
			isIssueInCategory,
			createAnalyticsEvent,
			startExperience,
			analyticsSuccess,
			analyticsFailure,
		],
	);

	return useCallback(
		({
			issueIds,
			targetColumn,
			getRankPromise,
			rollback,
		}: {
			issueIds: number[];
			targetColumn: BoardColumn;
			getRankPromise: (issueId: number) => () => Promise<void>;
			rollback: () => void;
		}) =>
			pushToJobQueue(() =>
				issueIds
					.map(
						(issueId) => () => getMoveBetweenGroupsPromise(issueId, targetColumn, getRankPromise),
					)
					.reduce(
						(previousPromise, currentTransitionAndRankPromise) =>
							previousPromise.then(currentTransitionAndRankPromise).catch((err) => {
								rollback();
								throw err;
							}),
						Promise.resolve(),
					)
					.then(() => Promise.resolve()),
			),
		[getMoveBetweenGroupsPromise, pushToJobQueue],
	);
};
